import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Banner } from '@shopify/polaris';
import { fetchShopStatus, setShopDomain } from '../utils/api';

export default function HomePage() {
  const { data: status, isLoading, error } = useQuery({
    queryKey: ['shopStatus'],
    queryFn: fetchShopStatus,
  });

  // Cache shop domain when status is loaded
  useEffect(() => {
    if (status?.shopDomain) {
      setShopDomain(status.shopDomain);
    }
  }, [status?.shopDomain]);

  const stats = status?.stats ?? {
    totalFulfillments: 0,
    pendingFulfillments: 0,
    completedToday: 0,
    failedToday: 0,
  };

  // Get shop from URL params for auth redirect
  const urlParams = new URLSearchParams(window.location.search);
  const shop = urlParams.get('shop') || status?.shopDomain;

  const handleAuthorize = () => {
    if (shop) {
      // Use App Bridge to redirect to OAuth flow (works in embedded iframe)
      const authUrl = `/auth?shop=${encodeURIComponent(shop)}`;

      // Check if we're in an embedded context with App Bridge
      if (window.shopify?.idToken) {
        // App Bridge is available - use top-level redirect
        // This breaks out of the iframe properly
        window.open(authUrl, '_top');
      } else {
        // Fallback for non-embedded context
        window.location.href = authUrl;
      }
    }
  };

  return (
    <div>
      {/* Shopify Authorization Banner */}
      {!isLoading && status && !status.shopifyConnected && shop && (
        <div style={{ marginBottom: '16px' }}>
          <Banner
            title="Shopify Authorization Required"
            tone="warning"
            action={{
              content: 'Authorize App',
              onAction: handleAuthorize,
            }}
          >
            <p>
              This app needs to be authorized to access your Shopify store.
              Click the button to complete the OAuth authorization flow.
            </p>
          </Banner>
        </div>
      )}

      {/* Connection Status */}
      <div className="jitsu-card">
        <h3 className="jitsu-card-title">Connection Status</h3>
        <div className="connection-status-row">
          <StatusItem
            label="Shopify"
            status={status?.shopifyConnected ? 'connected' : 'disconnected'}
            loading={isLoading}
          />
          <StatusItem
            label="Jitsu API"
            status={status?.jitsuConnected ? 'connected' : 'disconnected'}
            loading={isLoading}
          />
          <StatusItem
            label="Shipping Service"
            status={status?.fulfillmentServiceActive ? 'connected' : 'disconnected'}
            loading={isLoading}
          />
          {status?.features?.carrierServiceEnabled && (
            <StatusItem
              label="Carrier Service"
              status={status?.carrierServiceActive ? 'connected' : 'disconnected'}
              loading={isLoading}
            />
          )}
        </div>
        {error && (
          <p style={{ color: 'var(--jitsu-sienna)', marginTop: '12px' }}>
            Failed to load status: {(error as Error).message}
          </p>
        )}
      </div>

      {/* Stats Grid */}
      <div className="jitsu-card">
        <h3 className="jitsu-card-title">Shipping Statistics</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">
              {isLoading ? '...' : stats.totalFulfillments.toLocaleString()}
            </div>
            <div className="stat-label">Total Shipments</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {isLoading ? '...' : stats.pendingFulfillments.toLocaleString()}
            </div>
            <div className="stat-label">Pending</div>
          </div>
          <div className="stat-card">
            <div className="stat-value success">
              {isLoading ? '...' : stats.completedToday.toLocaleString()}
            </div>
            <div className="stat-label">Completed Today</div>
          </div>
          <div className="stat-card">
            <div className="stat-value error">
              {isLoading ? '...' : stats.failedToday.toLocaleString()}
            </div>
            <div className="stat-label">Failed Today</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="jitsu-card">
        <h3 className="jitsu-card-title">Quick Start</h3>
        <p style={{ color: 'var(--jitsu-mid-grey)', marginBottom: '16px' }}>
          Get started by configuring your Jitsu API credentials in the Settings tab.
        </p>
        <img
          src="/images/jitsu-comet.gif"
          alt="Jitsu"
          style={{ height: '40px', opacity: 0.8 }}
        />
      </div>
    </div>
  );
}

interface StatusItemProps {
  label: string;
  status: 'connected' | 'disconnected';
  loading?: boolean;
}

function StatusItem({ label, status, loading }: StatusItemProps) {
  const isConnected = status === 'connected';

  return (
    <div className="connection-item">
      <span style={{ color: 'var(--jitsu-mid-grey)', fontSize: '14px' }}>{label}</span>
      {loading ? (
        <span className="status-badge pending">Loading...</span>
      ) : (
        <span className={`status-badge ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? '✓ Connected' : '✗ Not Connected'}
        </span>
      )}
    </div>
  );
}

